import isEmpty from 'lodash/isEmpty';
import isEqual from 'lodash/isEqual';
import { connect } from 'react-redux';
import { withRouter } from 'react-router';

import {
    mapDispatchToProps as sourceMapDispatchToProps,
    mapStateToProps as sourceMapStateToProps,
    ProductPageContainer as AdvoxProductPageContainer,
} from 'AdvoxRoute/ProductPage/ProductPage.container';
import ProductAttachmentsQuery from 'Query/ProductAttachments.query';
import ProductTagsQuery from 'Query/ProductTags.query';
import { LOADING_TIME } from 'Route/CategoryPage/CategoryPage.config';
import ProductReducer from 'Store/Product/Product.reducer';
import { clearRelatedProductList } from 'Store/ProductRelatedAccessories/ProductRelatedAccessories.action';
import { withReducers } from 'Util/DynamicReducer';
import ElasticSuite from 'Util/ElasticSuite';
import { debounce, fetchQuery } from 'Util/Request';

/** @namespace Pwa/Route/ProductPage/Container/mapStateToProps */
export const mapStateToProps = (state) => ({
    ...sourceMapStateToProps(state),
    trackerVariables: state.ProductReducer.trackerVariables,
});

export const BreadcrumbsDispatcher = import(
    /* webpackMode: "lazy", webpackChunkName: "dispatchers" */
    'Store/Breadcrumbs/Breadcrumbs.dispatcher'
);

/** @namespace Pwa/Route/ProductPage/Container/mapDispatchToProps */
export const mapDispatchToProps = (dispatch) => ({
    ...sourceMapDispatchToProps(dispatch),
    clearRelatedProductList: () => dispatch(clearRelatedProductList()),
    updateBreadcrumbs: (breadcrumbs, prevCategoryId, fromSearch) =>
        BreadcrumbsDispatcher.then(({ default: dispatcher }) =>
            dispatcher.updateWithProduct(breadcrumbs, prevCategoryId, fromSearch, dispatch)
        ),
});

/** @namespace Pwa/Route/ProductPage/Container */
export class ProductPageContainer extends AdvoxProductPageContainer {
    debounceSendElasticVariables = debounce(() => {
        const { trackerVariables } = this.props;
        ElasticSuite.addPageVars(trackerVariables);
    }, 2000);

    state = {
        ...this.state,
        productTags: [],
        productAttachments: [],
    };

    componentDidMount() {
        this.requestProduct();

        super.componentDidMount.apply(this);

        this.requestProductAttchaments();
        this.clearRelatedProductList();
    }

    componentDidUpdate(prevProps) {
        const {
            isOffline,
            productSKU,
            product: { sku },
            trackerVariables,
        } = this.props;

        const {
            productSKU: prevProductSKU,
            product: { sku: prevSku },
        } = prevProps;

        const { sku: stateSKU } = history?.state?.state?.product || {};

        if (isOffline) {
            debounce(this.setOfflineNoticeSize, LOADING_TIME)();
        }

        if (productSKU !== prevProductSKU && stateSKU === productSKU) {
            this.updateHeaderState();
        }

        if (sku !== prevSku) {
            this.updateBreadcrumbs();
            this.updateHeaderState();
            this.updateMeta();
        }

        this._addToRecentlyViewedProducts();

        if (!isEqual(prevProps.trackerVariables, trackerVariables) && !isEmpty(trackerVariables)) {
            this.debounceSendElasticVariables();
        }
    }

    containerFunctions = {
        ...this.containerFunctions,
        requestProductTags: this.requestProductTags.bind(this),
    };

    clearRelatedProductList() {
        const { clearRelatedProductList } = this.props;

        clearRelatedProductList();
    }

    updateMeta() {
        const { updateMetaFromProduct } = this.props;
        const products = this.getDataSource();
        updateMetaFromProduct(products);
    }

    updateBreadcrumbs() {
        const { updateBreadcrumbs, location } = this.props;
        const { state: { prevCategoryId = null, fromSearch = false } = {} } = location;
        updateBreadcrumbs(this.getDataSource(), prevCategoryId, fromSearch);
    }

    requestProductAttchaments() {
        const product = this.props;
        fetchQuery(ProductAttachmentsQuery.getProductAttachmentsQuery(product.productSKU))
            .then(
                /** @namespace Pwa/Route/ProductPage/Container/ProductPageContainer/requestProductAttchaments/then/catch/fetchQuery/then */
                ({ products: { items } }) => {
                    this.setState({
                        productAttachments: items[0].mw_attachments.items,
                    });
                }
            )
            .catch(
                /** @namespace Pwa/Route/ProductPage/Container/ProductPageContainer/requestProductAttchaments/then/catch */
                () => []
            );
    }

    requestProductTags(activeProduct) {
        const { id } = activeProduct;

        if (id) {
            fetchQuery(ProductTagsQuery.getProductTagsQuery(id))
                .then(
                    /** @namespace Pwa/Route/ProductPage/Container/ProductPageContainer/requestProductTags/then/catch/fetchQuery/then */
                    ({ productTagsByProductId: { items } }) => {
                        this.setState({ productTags: items });
                    }
                )
                .catch(
                    /** @namespace Pwa/Route/ProductPage/Container/ProductPageContainer/requestProductTags/then/catch */
                    () => []
                );
        }
    }

    isProductTagsTabEmpty() {
        const { productTags } = this.state;
        return productTags.length === 0 || (productTags.length === 1 && productTags[0].tag_code === null);
    }

    isProductAttachmentTabEmpty() {
        const { productAttachments } = this.state;
        return !productAttachments || productAttachments.length === 0;
    }

    getDataSource() {
        const { productSKU, product } = this.props;

        const { sku } = product;

        if (productSKU === sku) {
            return product;
        }

        return {};
    }

    containerProps() {
        return {
            ...super.containerProps(),
            isProductTagsTabEmpty: this.isProductTagsTabEmpty(),
            isProductAttachmentTabEmpty: this.isProductAttachmentTabEmpty(),
        };
    }
}

export default withReducers({
    ProductReducer,
})(withRouter(connect(mapStateToProps, mapDispatchToProps)(ProductPageContainer)));
